import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import type { Log } from '@blakeelearning/log'
import type School from 'teacher-ui-client/models/school'
import type Teacher from 'teacher-ui-client/models/teacher'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface AuthUser {
  id: number
  schoolId: number
  roles?: string[]
  type?: string
}

/**
 * Note, ids provided by the auth token are numbers.
 */
export interface DecodedAuthToken {
  data: {
    id: number
    roles: string[]
    school_id: number
    type: string
  }
}

export default class SessionService extends Service {
  @service log!: Log

  @tracked authUser?: AuthUser

  /**
   * The currently logged in teacher.
   * This is set very early in the app lifecycle, so we declare it as ! to save complexity in the majority of its usage.
   */
  @tracked currentTeacher!: Teacher & { id: string }

  /**
   * The school of the currently logged in teacher.
   * This is set very early in the app lifecycle, so we declare it as ! to save complexity in the majority of its usage.
   */
  @tracked currentSchool!: School & { id: string }

  /**
   * Uses the decoded token from authtoken, if it exists, to create and set the AuthUser object for the current user and
   * subscription.
   */
  setAuthUser(decodedToken: DecodedAuthToken) {
    if (decodedToken) {
      const {
        data: { id, roles, school_id, type },
      } = decodedToken

      const authUser = {
        id,
        roles,
        schoolId: school_id,
        type,
      }

      this.log.setPerson({ id: String(authUser.id) })
      this.authUser = authUser
    }
  }

  /**
   * Set the current teacher for the session.
   */
  setTeacher(teacher: Teacher) {
    if (hasId(teacher)) this.currentTeacher = teacher
  }

  /**
   * Set the current school for the session.
   */
  setSchool(school: School) {
    if (hasId(school)) this.currentSchool = school
  }
}
declare module '@ember/service' {
  interface Registry {
    session: SessionService
  }
}
